import { createSSRApp } from 'vue';
import { renderToString } from 'vue/server-renderer';
import { Map } from 'mapbox-gl';

import { objectToStringSorted } from '../utils/objects';

import FcwMapMarker, {
  type FcwMapMarkerProps,
} from '@/components/atoms/FcwMapMarker';
import FcwHatch from '@/components/organisms/FcwEntitiesMap/components/FcwHatch.vue';
import FcwServiceLogoMarker from '@/components/organisms/FcwEntitiesMap/components/FcwServiceLogoMarker.vue';

import type { FcwHatchProps } from '@/components/organisms/FcwEntitiesMap/components/FcwHatch.vue';
import type { FcwServiceLogoMarkerProps } from '@/components/organisms/FcwEntitiesMap/components/FcwServiceLogoMarker.vue';

// Copied from Mapbox GL source code as not exposed:
// Ref: https://github.com/mapbox/mapbox-gl-js/blob/360bfd24790b2660f7bd53e401a73e37a6cdd705/src/style/style_image.js#L11
export type StyleImageInterface = {
  width: number;
  height: number;
  data: Uint8Array | Uint8ClampedArray;
  render?: () => boolean;
  onAdd?: (map: Map, id: string) => void;
  onRemove?: () => void;
};

/**
 * We generate SVG icons front-side from a Vue component, using SSR methods so that no DOM is created
 * and only a string representing the SVG markup is returned, only on-the-demand, which is light and efficient.
 * It is more convenient as all the white label config is fetched front-side via Strapi.
 * From [our tests](https://github.com/birotaio/customer-website/commit/6774ed05f63e7489fda30a088286729b0b463fe9), we
 * discarded the server-side approach because it involved delays when loading all the icons, prefetching, caching should
 * have been done which all the subsequent trade-offs, and still the `new Image + img.onload` step was necessary. It also
 * involved fetching Strapi config server-side and hacky config (injecting vite plugin in rollup nitro plugins, CJS warning).
 * @param imageName - Name of the image to add to the map
 */
export async function addMapMarkerToMapboxMap(
  map: Ref<Map | undefined>,
  props: FcwMapMarkerProps
): Promise<void> {
  if (!map.value) return;

  const width = 128;
  const height = 128;

  const mapMarkerImage = new Image(width, height);
  const imageName = objectToStringSorted(props);

  if (map.value.hasImage(imageName)) return;

  const mapMarkerApp = createSSRApp({
    render: () => h(FcwMapMarker, props),
  });

  const html = await renderToString(mapMarkerApp);
  mapMarkerImage.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    html
  )}`;
  mapMarkerImage.onload = () => {
    if (map.value?.hasImage(imageName)) return;
    map.value?.addImage(imageName, mapMarkerImage);
  };
}

export async function addHatchToMapboxMap(
  map: Ref<Map | undefined>,
  props: FcwHatchProps
): Promise<void> {
  if (!map.value) return;

  const width = 4;
  const height = 4;

  const hatchImage = new Image(width, height);
  const imageName = 'hatch';

  if (map.value.hasImage(imageName)) return;

  const hatchApp = createSSRApp({
    render: () => h(FcwHatch, props),
  });

  const html = await renderToString(hatchApp);
  hatchImage.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(html)}`;

  hatchImage.onload = () => {
    if (map.value?.hasImage(imageName)) return;
    map.value?.addImage(imageName, hatchImage);
  };
}

export async function addServiceMarkerToMapboxMap(
  map: Ref<Map | undefined>,
  props: FcwServiceLogoMarkerProps
): Promise<void> {
  if (!map.value) return;

  const width = 112;
  const height = 112;

  const mapMarkerImage = new Image(width, height);
  const imageName = 'service-logo';

  if (map.value.hasImage(imageName)) return;

  const mapMarkerApp = createSSRApp({
    render: () => h(FcwServiceLogoMarker, props),
  });

  const html = await renderToString(mapMarkerApp);
  mapMarkerImage.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    html
  )}`;

  mapMarkerImage.onload = () => {
    if (map.value?.hasImage(imageName)) return;
    map.value?.addImage(imageName, mapMarkerImage);
  };
}

export function setCursorPointer(map: Ref<Map | undefined>): void {
  if (!map.value) return;
  map.value.getCanvas().style.cursor = 'pointer';
}

export function setCursorDefault(map: Ref<Map | undefined>): void {
  if (!map.value) return;
  map.value.getCanvas().style.cursor = '';
}
